import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styles from '../../assets/styles/Setting.module.scss';
import { useProjectContext } from '../../contexts/ProjectContext';
import ProjectSettingsLayout from '../../components/Setting/ProjectSettingsLayout';
import BudgetDetail from '../../components/BudgetDetail';
import type { BudgetPeriod, MonthlyTotalBudgetSummary, PeriodStatus } from '../../types/budget';
import {
  formatBudgetDateRange,
  formatBudgetPeriodLabel,
  getBudgetPeriodStatus,
  normalizeBudgetPeriod,
  shiftBudgetPeriod,
} from '../../utils/budget';

type TotalBudgetResponse = {
  next_month: MonthlyTotalBudgetSummary | null;
  this_month: MonthlyTotalBudgetSummary | null;
  past_1_month: MonthlyTotalBudgetSummary | null;
  past_2_month: MonthlyTotalBudgetSummary | null;
  past_3_month: MonthlyTotalBudgetSummary | null;
  past_4_month: MonthlyTotalBudgetSummary | null;
  past_5_month: MonthlyTotalBudgetSummary | null;
};

const TOTAL_BUDGET_RESPONSE_KEYS: Array<keyof TotalBudgetResponse> = [
  'next_month',
  'this_month',
  'past_1_month',
  'past_2_month',
  'past_3_month',
  'past_4_month',
  'past_5_month',
];

const buildBudgetPeriod = (summary: MonthlyTotalBudgetSummary): BudgetPeriod => {
  const status = getBudgetPeriodStatus(summary.period);
  const labelByStatus: Record<PeriodStatus, string> = {
    next: '来月(予定)',
    current: '今月',
    past: formatBudgetPeriodLabel(summary.period),
  };

  return {
    key: normalizeBudgetPeriod(summary.period),
    label: labelByStatus[status],
    dateRange: formatBudgetDateRange(summary.period, summary.days_in_month),
    status,
    isEditable: status !== 'past',
    data: {
      ...summary,
      period: normalizeBudgetPeriod(summary.period),
    },
  };
};

const buildUniqueBudgetPeriods = (summaries: MonthlyTotalBudgetSummary[]) => {
  const uniquePeriods = new Map<string, BudgetPeriod>();

  summaries.forEach((summary) => {
    const period = buildBudgetPeriod(summary);

    if (!uniquePeriods.has(period.key)) {
      uniquePeriods.set(period.key, period);
    }
  });

  return Array.from(uniquePeriods.values()).sort((a, b) => b.key.localeCompare(a.key));
};

const SettingBudget: React.FC = () => {
  const { selectedProjectId: rawSelectedProjectId } = useProjectContext();
  const selectedProjectId = rawSelectedProjectId ?? -1;

  const [periods, setPeriods] = useState<BudgetPeriod[]>([]);
  const [selectedPeriodKey, setSelectedPeriodKey] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setSelectedPeriodKey('');
  }, [selectedProjectId]);

  const fetchPeriods = useCallback(async () => {
    setIsLoading(true);

    try {
      let totalData: TotalBudgetResponse = {
        next_month: null,
        this_month: null,
        past_1_month: null,
        past_2_month: null,
        past_3_month: null,
        past_4_month: null,
        past_5_month: null,
      };

      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/get-total-budget?project_id=${selectedProjectId}`, {
          credentials: 'include',
        });

        if (response.ok) {
          totalData = await response.json();
        } else {
          console.error('Failed to fetch total budget');
        }
      } catch (error) {
        console.error('Network error fetching total budget', error);
      }

      const summaries: MonthlyTotalBudgetSummary[] = TOTAL_BUDGET_RESPONSE_KEYS
        .map((key) => totalData[key])
        .filter((summary): summary is MonthlyTotalBudgetSummary => summary !== null);

      const nextPeriods = buildUniqueBudgetPeriods(summaries);
      const currentPeriod = nextPeriods.find((period) => period.status === 'current');

      setPeriods(nextPeriods);
      setSelectedPeriodKey(currentPeriod?.key ?? nextPeriods[0]?.key ?? '');
    } catch (error) {
      console.error('Critical error while initializing budget periods:', error);
      setPeriods([]);
      setSelectedPeriodKey('');
    } finally {
      setIsLoading(false);
    }
  }, [selectedProjectId]);

  useEffect(() => {
    fetchPeriods();
  }, [fetchPeriods]);

  const selectedPeriod = useMemo(
    () => periods.find((period) => period.key === selectedPeriodKey),
    [periods, selectedPeriodKey]
  );
  const currentPeriod = useMemo(
    () => periods.find((period) => period.status === 'current'),
    [periods]
  );
  const previousPeriod = useMemo(() => {
    if (!selectedPeriod) return undefined;

    const previousPeriodKey = shiftBudgetPeriod(selectedPeriod.key, -1);
    return periods.find((period) => period.key === previousPeriodKey);
  }, [periods, selectedPeriod]);

  return (
    <ProjectSettingsLayout>
      <div className={styles.masterDetailContainer}>
        <div className={styles.sidebar}>
          <h4>来月</h4>
          {periods.filter((period) => period.status === 'next').map((period) => (
            <div
              key={period.key}
              className={`${styles.monthItem} ${selectedPeriodKey === period.key ? styles.active : ''}`}
              onClick={() => setSelectedPeriodKey(period.key)}
            >
              <div>
                <div className={styles.monthLabel}>{period.label}</div>
                <div className={styles.monthDate}>{period.dateRange}</div>
              </div>
              <div className={styles.sidebarArrow}>›</div>
            </div>
          ))}

          <h4>今月</h4>
          {periods.filter((period) => period.status === 'current').map((period) => (
            <div
              key={period.key}
              className={`${styles.monthItem} ${selectedPeriodKey === period.key ? styles.active : ''}`}
              onClick={() => setSelectedPeriodKey(period.key)}
            >
              <div>
                <div className={styles.monthLabel}>{period.label}</div>
                <div className={styles.monthDate}>{period.dateRange}</div>
              </div>
              <div className={styles.sidebarArrow}>›</div>
            </div>
          ))}

          <h4>過去</h4>
          {periods.filter((period) => period.status === 'past').map((period) => (
            <div
              key={period.key}
              className={`${styles.monthItem} ${selectedPeriodKey === period.key ? styles.active : ''}`}
              onClick={() => setSelectedPeriodKey(period.key)}
            >
              <div>
                <div className={styles.monthLabel}>{period.label}</div>
                <div className={styles.monthDate}>{period.dateRange}</div>
              </div>
              {selectedPeriodKey === period.key ? <div className={styles.sidebarArrow}>›</div> : null}
            </div>
          ))}
        </div>

        <div className={styles.mainContent}>
          {isLoading ? (
            <div className={styles.loadingText}>データ読み込み中...</div>
          ) : selectedPeriod ? (
            <BudgetDetail
              key={selectedPeriod.key}
              projectId={selectedProjectId}
              period={selectedPeriod}
              currentPeriod={currentPeriod}
              previousPeriod={previousPeriod}
              onRefreshPeriods={fetchPeriods}
            />
          ) : (
            <div className={styles.loadingText}>対象の予算データがありません</div>
          )}
        </div>
      </div>
    </ProjectSettingsLayout>
  );
};

export default SettingBudget;
